import { type FC, useCallback, useEffect, useState } from 'react';

import { Box } from '../Box';
import { Text, TFontWeight, TTextSize } from '../Typography';
import { rootStyles } from './Timer.styles';

export type TimeLeft = {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  totalSeconds: number;
};

export type TimerProps = {
  /**
   * Unix timestamp (in seconds) for when the timer expires
   */
  expirationTimestamp: number;
  /**
   * Callback fired every second with the remaining time
   */
  onTimeUpdate?: (timeLeft: TimeLeft) => void;
  /**
   * Callback fired when the timer reaches zero
   */
  onExpire?: () => void;
  /**
   * Additional CSS class names
   */
  className?: string;
  /**
   * Text to display before the time left
   */
  fontSize?: TTextSize;
  /**
   * Font weight of the text
   */
  fontWeight?: TFontWeight;
  /**
   * Color of the text
   */
  showSeconds?: boolean;
};

const calculateTimeLeft = (expirationTimestamp: number): TimeLeft | null => {
  const now = Math.floor(Date.now() / 1000);
  const diff = expirationTimestamp - now;

  if (diff <= 0) {
    return null;
  }

  const days = Math.floor(diff / (24 * 60 * 60));
  const hours = Math.floor((diff % (24 * 60 * 60)) / (60 * 60));
  const minutes = Math.floor((diff % (60 * 60)) / 60);
  const seconds = diff % 60;

  return {
    days,
    hours,
    minutes,
    seconds,
    totalSeconds: diff,
  };
};

const Timer: FC<TimerProps> = ({
  expirationTimestamp,
  onTimeUpdate,
  onExpire,
  className,
  fontSize = 'md',
  fontWeight = 'bold',
  showSeconds = false,
}) => {
  const [timeLeft, setTimeLeft] = useState<TimeLeft | null>(() =>
    calculateTimeLeft(expirationTimestamp)
  );

  const updateTime = useCallback(() => {
    const newTimeLeft = calculateTimeLeft(expirationTimestamp);
    setTimeLeft(newTimeLeft);

    if (newTimeLeft) {
      onTimeUpdate?.(newTimeLeft);
    } else {
      onExpire?.();
    }

    return newTimeLeft;
  }, [expirationTimestamp, onTimeUpdate, onExpire]);

  useEffect(() => {
    // Initial calculation
    const initialTime = updateTime();
    if (!initialTime) return;

    const interval = setInterval(() => {
      const remaining = updateTime();
      if (!remaining) {
        clearInterval(interval);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [updateTime]);

  if (!timeLeft) {
    return null;
  }

  return (
    <Box css={rootStyles} className={className}>
      <Text as="span" fontWeight={fontWeight} size={fontSize}>
        {timeLeft.days}d : {timeLeft.hours}h : {timeLeft.minutes}m
      </Text>
      {showSeconds && (
        <Text as="span" fontWeight={fontWeight} size={fontSize}>
          &nbsp;: {timeLeft.seconds}s
        </Text>
      )}
    </Box>
  );
};

Timer.displayName = 'Timer';

export default Timer;
